import React, { useRef, useEffect, useState } from 'react';
import { theme as T } from '../../theme';
import { useLoyaltyDemo } from '../../context/LoyaltyDemoContext';
import { CATERING_BASE, cateringPaths } from '../catering/paths';
import { PC_BASE, pcPaths } from '../productCatalog/paths';
import { DISPLAY_BASE, displayPaths } from '../display/paths';
import { getOwnerLocationCount } from '../../context/OwnerViewContext';
import { Bone } from '../Shimmer';
import { useAppSelector } from '../../redux/hooks';

function pcPathActive(pathname: string, href: string) {
  return pathname.startsWith(href);
}

/** Flat report nav links — used when pathname + navigate are passed. */
const REPORT_NAV_LINKS = [
  { id: 'eod', label: 'EOD Reconciliation', icon: 'credit_card', path: '/reports/eod' },
  { id: 'labor', label: 'Labor', icon: 'schedule', path: '/reports/labor' },
  { id: 'loyalty', label: 'Loyalty', icon: 'loyalty', path: '/reports/loyalty' },
  { id: 'checkin', label: 'Check-In Analytics', icon: 'how_to_reg', path: '/reports/checkin' },
  { id: 'reservation', label: 'Reservations', icon: 'event_available', path: '/reports/reservation' },
  { id: 'customers', label: 'Customers', icon: 'people', path: '/reports/customers' },
  { id: 'voice-agent', label: 'Voice Agent', icon: 'support_agent', path: '/reports/voice-agent' },
  { id: 'events', label: 'Events & Pre-Order', icon: 'celebration', path: '/reports/events' },
];

const Chev = ({ s = 10, c = T.t3, r = 0 }: { s?: number; c?: string; r?: number }) => (
  <svg width={s} height={s} viewBox="0 0 10 10" style={{ transform: `rotate(${r}deg)`, transition: 'transform 0.2s', flexShrink: 0 }}>
    <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const MSIcon = ({ name, size = 18, color = 'inherit', style = {} }: { name: string; size?: number; color?: string; style?: React.CSSProperties }) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, lineHeight: 1, color, userSelect: 'none', flexShrink: 0, ...style }}
  >
    {name}
  </span>
);

/**
 * Standalone Sidebar component.
 * When pathname + navigate are passed, builds full nav (Snapshot, Product + report groups) internally.
 * Otherwise uses navItems prop.
 * @param {Object} [props.pathname] - current path (e.g. from useLocation) — when set with navigate, builds default nav
 * @param {Function} [props.navigate] - router navigate — when set with pathname, builds default nav
 */
export default function Sidebar({ brand = {}, outlet, navItems: navItemsProp = [], user, pathname, navigate, showOwnerView: showOwnerViewProp }: any) {
  const outletRef = useRef(null);
  const userMenuRef = useRef(null);
  const useBuiltNav = pathname != null && typeof navigate === 'function';
  const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];
  const isSuperUser = SUPER_ROLES.includes(user?.role);
  const LOYALTY_ALLOWED_ROLES = ['Restaurant_Owner', 'Restaurant_Manager', 'Regional_Employee', 'Magil_Admin'];
  const isLoyaltyAllowed = LOYALTY_ALLOWED_ROLES.includes(user?.role);
  // Owner Dashboard (desktop) is only available to super-users with more than one
  // outlet — scenario 1. Single-outlet super-users and non-super users land on snapshot.
  // Callers that track outlet count reactively (DesktopLayout) should pass showOwnerView
  // directly — getOwnerLocationCount() is a plain module variable, not reactive, so
  // reading it here would only reflect whatever value happened to be set on this render.
  const showOwnerView = showOwnerViewProp !== undefined
    ? showOwnerViewProp
    : isSuperUser && (getOwnerLocationCount() ?? 0) > 1;

  const reportPaths = ['/reports', '/snapshot', '/eod', '/checkin', '/reservation', '/customers', '/events'];
  const isReportPath = reportPaths.some((p) => pathname.startsWith(p)) || pathname === '/product';
  const isDocsPath = pathname === '/docs';

  const DOC_SECTIONS = ['overview', 'filters', 'snapshot', 'sales', 'product', 'checkin', 'eod', 'reservation', 'customers', 'events'];
  const [activeDocSection, setActiveDocSection] = useState('overview');

  useEffect(() => {
    if (!isDocsPath) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveDocSection(entry.target.id.replace('section-', ''));
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    // Observe after a short delay to ensure the page has rendered
    const timer = setTimeout(() => {
      DOC_SECTIONS.forEach((id) => {
        const el = document.getElementById(`section-${id}`);
        if (el) observer.observe(el);
      });
    }, 300);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [isDocsPath]);

  const isProductCatalogPath = pathname.startsWith(PC_BASE);
  const isDisplayPath = pathname.startsWith(DISPLAY_BASE);

  const isCateringPath = pathname.startsWith(CATERING_BASE);
  const showCatering = outlet?.isCateringEnabled === true;
  
  const loyaltyPaths = ['/loyalty'];
  const isLoyaltyPath = loyaltyPaths.some((p) => pathname.startsWith(p));

  const loyaltyProgram = useAppSelector((s) => s.loyaltyProgram.program);
  const loyaltyProgramIsLive = loyaltyProgram != null;

  const loyaltyGroupChildren = [
    { type: 'link', id: 'loyalty-program', label: 'Program', icon: 'settings', active: pathname === '/loyalty' || pathname === '/loyalty/' || pathname.startsWith('/loyalty/program'), onClick: () => navigate('/loyalty/program') },
    { type: 'link', id: 'loyalty-rewards', label: 'Rewards', icon: 'card_giftcard', active: pathname.startsWith('/loyalty/rewards') || pathname.startsWith('/loyalty/automated-rewards'), onClick: () => navigate('/loyalty/rewards'), disabled: !loyaltyProgramIsLive },
    { type: 'link', id: 'loyalty-members', label: 'Members', icon: 'group', active: pathname.startsWith('/loyalty/members'), onClick: () => navigate('/loyalty/members'), disabled: !loyaltyProgramIsLive },
    { type: 'link', id: 'loyalty-notifications', label: 'Notifications', icon: 'notifications', active: pathname.startsWith('/loyalty/notifications'), onClick: () => navigate('/loyalty/notifications'), disabled: !loyaltyProgramIsLive },
  ];
  const loyaltyGroupActive = loyaltyGroupChildren.some((c) => c.active);

  const isReportsRoot = pathname === '/reports' || pathname === '/reports/';
  const reportsGroupChildren = [
    { type: 'link', id: 'snapshot', label: 'Snapshot', icon: 'flash_on', active: pathname.includes('snapshot') || isReportsRoot, onClick: () => navigate('/reports/snapshot') },
    { type: 'link', id: 'sales', label: 'Sales', icon: 'bar_chart', active: pathname === '/reports/sales' || pathname.startsWith('/reports/sales/'), onClick: () => navigate('/reports/sales') },
    { type: 'link', id: 'product', label: 'Product', icon: 'restaurant_menu', active: pathname === '/reports/product' || pathname.startsWith('/reports/product/'), onClick: () => navigate('/reports/product') },
    { type: 'link', id: 'offers', label: 'Offers', icon: 'local_offer', active: pathname === '/reports/offers' || pathname.startsWith('/reports/offers/'), onClick: () => navigate('/reports/offers') },
    ...REPORT_NAV_LINKS.map((link) => ({
      type: 'link',
      id: link.id,
      label: link.label,
      icon: link.icon,
      active: pathname.startsWith(link.path),
      onClick: () => navigate(link.path),
    })),
  ];
  const reportsGroupActive = reportsGroupChildren.some((c) => c.active);

  const cateringGroupChildren = [
    { type: 'link', id: 'catering-leads', label: 'Leads', icon: 'contact_phone', active: pathname.startsWith(cateringPaths.leads), onClick: () => navigate(cateringPaths.leads) },
    { type: 'link', id: 'catering-estimates', label: 'Estimates', icon: 'request_quote', active: pathname.startsWith(cateringPaths.estimates), onClick: () => navigate(cateringPaths.estimates) },
    { type: 'link', id: 'catering-orders', label: 'Orders', icon: 'receipt_long', active: pathname === cateringPaths.orders || (pathname.startsWith(cateringPaths.orders) && !pathname.startsWith(cateringPaths.prepList) && !pathname.startsWith(cateringPaths.recurringOrders)), onClick: () => navigate(cateringPaths.orders) },
    // { type: 'link', id: 'catering-prep-list', label: 'Prep List', icon: 'checklist', active: pathname.startsWith(cateringPaths.prepList), onClick: () => navigate(cateringPaths.prepList) },
    { type: 'link', id: 'catering-invoices', label: 'Invoices', icon: 'receipt', active: pathname.startsWith(cateringPaths.invoices), onClick: () => navigate(cateringPaths.invoices) },
    { type: 'link', id: 'catering-customers', label: 'Customers', icon: 'people', active: pathname.startsWith(cateringPaths.customers), onClick: () => navigate(cateringPaths.customers) },
    { type: 'link', id: 'catering-packages', label: 'Packages', icon: 'inventory_2', active: pathname.startsWith(cateringPaths.packages), onClick: () => navigate(cateringPaths.packages) },
    { type: 'link', id: 'catering-settings', label: 'Settings', icon: 'settings', active: pathname.startsWith(cateringPaths.settings) || pathname.startsWith(cateringPaths.emailTemplates), onClick: () => navigate(cateringPaths.settings) },
  ];
  const cateringGroupActive = isCateringPath || cateringGroupChildren.some((c) => c.active);
  const isMaghilAiPath = pathname.startsWith('/reports/voice-agent');
  const maghilAiGroupChildren = [
    { type: 'link', id: 'voice-agent', label: 'Voice Agent', icon: 'support_agent', active: isMaghilAiPath, onClick: () => navigate('/reports/voice-agent') },
  ];
  const maghilAiGroupActive = maghilAiGroupChildren.some((c) => c.active);


  const productCatalogGroupChildren = [
    { type: 'link', id: 'pc-availability', label: 'Control Center', icon: 'dashboard', active: pcPathActive(pathname, pcPaths.availability), onClick: () => navigate(pcPaths.availability) },
    { type: 'link', id: 'pc-items', label: 'Items', icon: 'inventory_2', active: pcPathActive(pathname, pcPaths.items), onClick: () => navigate(pcPaths.items) },
    // { type: 'link', id: 'pc-menus', label: 'Menus', icon: 'restaurant_menu', active: pcPathActive(pathname, pcPaths.menus), onClick: () => navigate(pcPaths.menus) },
    { type: 'link', id: 'pc-categories', label: 'Categories', icon: 'category', active: pcPathActive(pathname, pcPaths.categories), onClick: () => navigate(pcPaths.categories) },
    { type: 'link', id: 'pc-modifiers', label: 'Modifiers', icon: 'tune', active: pcPathActive(pathname, pcPaths.modifiers), onClick: () => navigate(pcPaths.modifiers) },
    // { type: 'link', id: 'pc-variations', label: 'Variations', icon: 'checklist', active: pcPathActive(pathname, pcPaths.variations), onClick: () => navigate(pcPaths.variations) },
    // { type: 'link', id: 'pc-combos', label: 'Combos', icon: 'fastfood', active: pcPathActive(pathname, pcPaths.combos), onClick: () => navigate(pcPaths.combos) },
    // { type: 'link', id: 'pc-set-menus', label: 'Set Menus', icon: 'restaurant', active: pcPathActive(pathname, pcPaths.setMenus), onClick: () => navigate(pcPaths.setMenus) },
    { type: 'link', id: 'pc-kitchen-stations', label: 'Kitchen Stations', icon: 'countertops', active: pcPathActive(pathname, pcPaths.kitchenStations), onClick: () => navigate(pcPaths.kitchenStations) },
    { type: 'link', id: 'pc-sessions', label: 'Sessions', icon: 'schedule', active: pcPathActive(pathname, pcPaths.sessions), onClick: () => navigate(pcPaths.sessions) },
    { type: 'link', id: 'pc-sync-history', label: 'Sync History', icon: 'sync', active: pcPathActive(pathname, pcPaths.syncHistory), onClick: () => navigate(pcPaths.syncHistory) },
  ];
  const productCatalogGroupActive = productCatalogGroupChildren.some((c) => c.active);

  const isMediaLibraryPath = pathname.startsWith(displayPaths.mediaLibrary);
  const isDisplayListPath = isDisplayPath && !isMediaLibraryPath;
  const displayGroupChildren = [
    { type: 'link', id: 'display-media-library', label: 'Media Library', icon: 'perm_media', active: isMediaLibraryPath, onClick: () => navigate(displayPaths.mediaLibrary) },
    { type: 'link', id: 'display-list', label: 'Display List', icon: 'dashboard', active: isDisplayListPath, onClick: () => navigate(displayPaths.root) },
  ];
  const displayGroupActive = isDisplayPath;

  const scrollToDocSection = (sectionId: string) => {
    if (pathname !== '/docs') {
      navigate('/docs');
      setTimeout(() => document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const docsGroupChildren = [
    { type: 'link', id: 'docs-overview', label: 'Getting Started', icon: 'rocket_launch', active: activeDocSection === 'overview', onClick: () => scrollToDocSection('overview') },
    { type: 'link', id: 'docs-filters', label: 'Date Filters', icon: 'date_range', active: activeDocSection === 'filters', onClick: () => scrollToDocSection('filters') },
    { type: 'link', id: 'docs-snapshot', label: 'Snapshot', icon: 'flash_on', active: activeDocSection === 'snapshot', onClick: () => scrollToDocSection('snapshot') },
    { type: 'link', id: 'docs-sales', label: 'Sales', icon: 'bar_chart', active: activeDocSection === 'sales', onClick: () => scrollToDocSection('sales') },
    { type: 'link', id: 'docs-product', label: 'Product & Menu', icon: 'restaurant_menu', active: activeDocSection === 'product', onClick: () => scrollToDocSection('product') },
    { type: 'link', id: 'docs-checkin', label: 'Check-In', icon: 'how_to_reg', active: activeDocSection === 'checkin', onClick: () => scrollToDocSection('checkin') },
    { type: 'link', id: 'docs-eod', label: 'EOD Summary', icon: 'credit_card', active: activeDocSection === 'eod', onClick: () => scrollToDocSection('eod') },
    { type: 'link', id: 'docs-reservation', label: 'Reservations', icon: 'event_available', active: activeDocSection === 'reservation', onClick: () => scrollToDocSection('reservation') },
    { type: 'link', id: 'docs-customers', label: 'Customers', icon: 'people', active: activeDocSection === 'customers', onClick: () => scrollToDocSection('customers') },
    { type: 'link', id: 'docs-events', label: 'Events & Pre-Orders', icon: 'celebration', active: activeDocSection === 'events', onClick: () => scrollToDocSection('events') },
  ];
  const docsGroupActive = isDocsPath;

  const ownerViewLink = {
    type: 'link',
    id: 'owner-view',
    label: 'Owner Dashboard',
    icon: 'domain',
    active: pathname === '/owner-view',
    onClick: () => navigate('/owner-view'),
  };

  const builtNavItems = useBuiltNav
    ? isDocsPath
      ? [
          {
            type: 'group',
            id: 'docs',
            label: 'Docs',
            icon: 'menu_book',
            active: docsGroupActive,
            children: docsGroupChildren,
          },
        ]
      : [
          ...(showOwnerView ? [ownerViewLink] : []),
          // owner-view hidden from desktop sidebar
          {
            type: 'link',
            id: 'employees',
            label: 'Employees',
            icon: 'group',
            active: pathname.startsWith('/employees'),
            onClick: () => navigate('/employees'),
          },
          {
            type: 'group',
            id: 'reports',
            label: 'Reports',
            icon: 'bar_chart',
            active: reportsGroupActive,
            children: reportsGroupChildren,
          },
          ...(showCatering ? [{
            type: 'group',
            id: 'catering',
            label: 'Catering',
            icon: 'lunch_dining',
            active: cateringGroupActive,
            children: cateringGroupChildren,
          }] : []),
          {
          type: 'group',
          id: 'productCatalog',
          label: 'Product Catalog',
          icon: 'storefront',
          active: productCatalogGroupActive,
          children: productCatalogGroupChildren,
        },
        // Loyalty — restricted roles see a plain link (no dropdown)
        isLoyaltyAllowed
           ? {
              type: 'group',
              id: 'loyalty',
              label: 'Loyalty',
              icon: 'loyalty',
              active: loyaltyGroupActive,
              children: loyaltyGroupChildren,
            }
          : {
              type: 'link',
              id: 'loyalty',
              label: 'Loyalty',
              icon: 'loyalty',
              active: isLoyaltyPath,
              onClick: () => navigate('/loyalty'),
            },
        {
          type: 'group',
          id: 'display',
          label: 'Display',
          icon: 'tv',
          active: displayGroupActive,
          children: displayGroupChildren,
        },
        {
          type: 'link',
          id: 'offers',
          label: 'Offers',
          icon: 'badge',
          active: pathname === '/offers' || pathname.startsWith('/offers/'),
          onClick: () => navigate('/offers', { state: { resetOffersTab: true, ts: Date.now() } }),
        },
        // {
        //   type: 'group',
        //   id: 'reports',
        //   label: 'Reports',
        //   icon: 'bar_chart',
        //   active: reportsGroupActive,
        //   children: reportsGroupChildren,
        // },
      ]
    : [];

  const navItems = useBuiltNav ? builtNavItems : navItemsProp;

  const allGroupsClosed = { reports: false, loyalty: false, productCatalog: false, docs: false, display: false, catering: false};
  const [groupExpanded, setGroupExpanded] = useState(() => {
    if (isProductCatalogPath) return { ...allGroupsClosed, productCatalog: true };
    if (isCateringPath) return { ...allGroupsClosed, catering: true };
    if (isLoyaltyPath) return { ...allGroupsClosed, loyalty: true };
    if (isReportPath) return { ...allGroupsClosed, reports: true };
    if (isDocsPath) return { ...allGroupsClosed, docs: true };
    if (isDisplayPath) return { ...allGroupsClosed, display: true };
    return allGroupsClosed;
  });
  useEffect(() => {
    if (isReportPath) setGroupExpanded({ ...allGroupsClosed, reports: true });
  }, [isReportPath]);

  useEffect(() => {
     if (isLoyaltyPath && isLoyaltyAllowed) setGroupExpanded({ ...allGroupsClosed, loyalty: true });
  }, [isLoyaltyPath, isLoyaltyAllowed]);
  useEffect(() => {
    if (isProductCatalogPath) setGroupExpanded({ ...allGroupsClosed, productCatalog: true });
  }, [isProductCatalogPath]);
  useEffect(() => {
    if (isDisplayPath) setGroupExpanded({ ...allGroupsClosed, display: true });
  }, [isDisplayPath]);
  useEffect(() => {
    if (isCateringPath) setGroupExpanded({ ...allGroupsClosed, catering: true });
  }, [isCateringPath]);
  useEffect(() => {
    if (isDocsPath) setGroupExpanded({ ...allGroupsClosed, docs: true });
  }, [isDocsPath]);
  const toggleGroup = (groupId: string) => setGroupExpanded((prev: any) => {
    const isCurrentlyOpen = prev[groupId];
    // Close all, then open the clicked one (if it was closed)
    return { ...allGroupsClosed, [groupId]: !isCurrentlyOpen };
  });

  const {
    logoLetters = 'M',
    brandName = 'Maghil',
    subtitle = 'ANALYTICS',
  } = brand;

  const outletLogoUrl = outlet?.logoUrl ?? null;
  const outletLocation = outlet?.location ?? '';
  const outletDisplayName = outlet?.name ?? outlet?.label ?? brandName;

  const hasOutletDropdown = outlet?.options != null && outlet.options.length > 0;
  const readOnlyOutletLabelOnly = outlet?.readOnlyLabelOnly === true;
  const outletOpen = outlet?.open ?? false;
  const outletToggle = outlet?.onToggle;
  // True until the selected outlet is resolved (name available). On native the
  // auth/branch storage hydrates asynchronously, so this stays true from the first
  // paint — we show shimmer in place of the logo + dropdown instead of flashing the
  // "Select outlet" placeholder and the fallback brand mark before data arrives.
  const outletDetailsLoading = outlet?.detailsLoading ?? false;

  useEffect(() => {
    if (!outletToggle || !outletOpen) return;
    const handleClickOutside = (e) => {
      if (outletRef.current && !outletRef.current.contains(e.target)) outletToggle();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [outletOpen, outletToggle]);

  const [userOpen, setUserOpen] = useState(false);
  const [outletLogoError, setOutletLogoError] = useState(false);
  useEffect(() => {
    setOutletLogoError(false);
  }, [outletLogoUrl]);

  useEffect(() => {
    if (!user?.onLogout) return;
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [user?.onLogout]);

  return (
    <aside
      style={{
        width: 250,
        minWidth: 250,
        background: T.surface,
        borderRight: `1px solid ${T.div}`,
        boxShadow: T.sbSh,
        position: 'relative',
        height: '100%',
        minHeight: 0,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontFamily: T.f,
        zIndex: 2,
      }}
    >
      {/* Outlet profile header (logo, name, location) like Maghil portal */}
      {outlet ? (
        <div style={{ padding: 20, borderBottom: `1px solid ${T.div}`, textAlign: 'center' }}>
          <div
            style={{
              borderRadius: 0,
              margin: '0 auto 12px',
              overflow: 'hidden',
              // background: outletLogoUrl && !outletLogoError ? T.hover : 'linear-gradient(135deg,#D23A3B,#E86B6B)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              maxHeight: 64,
              width: 'auto',
            }}
          >
            {outletDetailsLoading && !outletLogoUrl ? (
              <Bone w={120} h={48} r={6} />
            ) : outletLogoUrl && !outletLogoError ? (
              <img
                src={outletLogoUrl}
                alt=""
                style={{ maxHeight: 64, width: 'auto', height: 'auto', objectFit: 'contain' }}
                onError={() => setOutletLogoError(true)}
              />
            ) : (
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 22 }}>
                {(logoLetters || outletDisplayName?.slice(0, 2) || 'M').toString().slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          {outletDetailsLoading ? (
            <Bone w={120} h={15} r={6} style={{ margin: '0 auto 4px' }} />
          ) : (
            <div style={{ fontSize: 15, fontWeight: 700, color: T.t1, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {outletDisplayName}
            </div>
          )}
          {outletDetailsLoading ? (
            <Bone w={80} h={11} r={6} style={{ margin: '0 auto 12px' }} />
          ) : outletLocation ? (
            <div style={{ fontSize: 12, color: T.t3, marginBottom: 12 }}>{outletLocation}</div>
          ) : null}
        </div>
      ) : (
        /* Logo / Brand when no outlet */
        <div style={{ padding: 20, borderBottom: `1px solid ${T.div}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: 'linear-gradient(135deg,#D23A3B,#E86B6B)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {(logoLetters || brandName?.slice(0, 2) || 'M').toString().slice(0, 2).toUpperCase()}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {brandName}
              </div>
              {subtitle && (
                <div style={{ fontSize: 10, color: T.t3, letterSpacing: 0.5 }}>{subtitle}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Outlet selector (dropdown to switch branches) */}
      {outlet && (
        <div ref={outletRef} style={{ padding: '12px 16px', position: 'relative' }}>
          {outletDetailsLoading ? (
            <Bone w="100%" h={34} r={8} />
          ) : hasOutletDropdown ? (
            <>
              <div
                onClick={outletToggle}
                style={{
                  background: T.hover,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {outlet.label}
                </span>
                <Chev s={10} c={T.t3} r={outletOpen ? 180 : 0} />
              </div>
              {outletOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 16,
                    right: 16,
                    marginTop: 4,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: T.sh,
                    maxHeight: 220,
                    overflow: 'auto',
                    zIndex: 100,
                  }}
                >
                  {outlet.loading ? (
                    <div style={{ padding: 12, fontSize: 12, color: T.t3 }}>Loading...</div>
                  ) : outlet.options.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: T.t3 }}>No branches</div>
                  ) : (
                    outlet.options.map((opt) => {
                      const id = typeof opt === 'object' && opt !== null && 'id' in opt ? opt.id : opt;
                      const label = typeof opt === 'object' && opt !== null && 'label' in opt ? opt.label : String(opt);
                      const isSelected = id === outlet.selectedId;
                      return (
                        <div
                          key={id}
                          onClick={() => outlet.onSelect(opt)}
                          style={{
                            padding: '10px 12px',
                            fontSize: 12,
                            fontWeight: isSelected ? 600 : 500,
                            cursor: 'pointer',
                            background: isSelected ? T.accentSoft : 'transparent',
                            color: isSelected ? T.accent : T.t1,
                          }}
                        >
                          {label}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                background: T.hover,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: '8px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span>{outlet.label}</span>
              <Chev s={10} c={T.t3} />
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, padding: '4px 8px', overflowY: 'auto' }}>
        {navItems.map((item) => {
          if (item.type === 'group') {
            const expanded = groupExpanded[item.id] !== false;
            const isActive = item.active;
            return (
              <div key={item.id} style={{ padding: '0 8px', marginBottom: 2 }}>
                <div
                  onClick={() => {
                    if (item.id === 'reports' && useBuiltNav) {
                      if (!expanded && !isReportPath) navigate('/reports/snapshot');
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'productCatalog' && useBuiltNav) {
                      if (!expanded && !isProductCatalogPath) navigate(pcPaths.availability);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'loyalty' && useBuiltNav) {
                      if (!expanded && !isLoyaltyPath) navigate('/loyalty/program');
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'display' && useBuiltNav) {
                      if (!expanded && !isDisplayPath) navigate(displayPaths.root);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'catering' && useBuiltNav) {
                      // Lands on the first Catering page the employee can open (or Access Restricted) — see CateringLanding.
                      if (!expanded && !isCateringPath) navigate(cateringPaths.start);
                      toggleGroup(item.id);
                      return;
                    }
                    toggleGroup(item.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '9px 10px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    fontSize: 12.5,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? T.accent : T.t2,
                    background: isActive ? T.accentSoft : 'transparent',
                    borderLeft: isActive ? `2px solid ${T.accent}` : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {item.icon && <MSIcon name={item.icon} size={18} color={isActive ? T.accent : T.t3} />}
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <Chev s={10} c={isActive ? T.accent : T.t3} r={expanded ? 180 : 0} />
                </div>
                {expanded &&
                  (item.children || []).map((child) => {
                    const childActive = child.active;
                    const childDisabled = child.disabled;
                    const childClick = childDisabled ? undefined : child.onClick;
                    return (
                      <div key={child.id} style={{ padding: '0 8px', marginBottom: 2, paddingLeft: 24, opacity: childDisabled ? 0.4 : 1 }}>
                        <div
                          onClick={childClick}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '9px 10px',
                            cursor: childDisabled ? 'not-allowed' : childClick ? 'pointer' : 'default',
                            borderRadius: 6,
                            fontSize: 12.5,
                            fontWeight: childActive ? 600 : 400,
                            color: childActive ? T.accent : T.t2,
                            background: childActive ? T.accentSoft : 'transparent',
                            borderLeft: childActive ? `2px solid ${T.accent}` : '2px solid transparent',
                            transition: 'all 0.15s',
                          }}
                        >
                          {child.icon && <MSIcon name={child.icon} size={18} color={childActive ? T.accent : T.t3} />}
                          <span>{child.label}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          }
          const isActive = item.active;
          const handleClick = item.onClick ?? (item.path ? () => {} : undefined);
          return (
            <div key={item.id} style={{ padding: '0 8px', marginBottom: 2 }}>
              <div
                onClick={handleClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 10px',
                  cursor: handleClick ? 'pointer' : 'default',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? T.accent : T.t2,
                  background: isActive ? T.accentSoft : 'transparent',
                  borderLeft: isActive ? `2px solid ${T.accent}` : '2px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {item.icon && <MSIcon name={item.icon} size={18} color={isActive ? T.accent : T.t3} />}
                <span>{item.label}</span>
              </div>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      {user && (
        <div ref={userMenuRef} style={{ padding: '16px 20px', borderTop: `1px solid ${T.div}`, position: 'relative' }}>
          {user.onLogout ? (
            <>
              <div
                onClick={() => setUserOpen(!userOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '4px 0',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    background: T.hover,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.t2,
                  }}
                >
                  {user.initials}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: 10, color: T.t3 }}>{user.role}</div>
                </div>
                <Chev s={10} c={T.t3} r={userOpen ? 180 : 0} />
              </div>
              {userOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 20,
                    right: 20,
                    marginBottom: 4,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: T.sh,
                    zIndex: 100,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => { user.onLogout(); setUserOpen(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.red,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  background: T.hover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.t2,
                }}
              >
                {user.initials}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{user.name}</div>
                <div style={{ fontSize: 10, color: T.t3 }}>{user.role}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
